/**
 * Roleta da Sorte — espelho dos enums e payloads do pontuax-backend.
 *
 * Fonte: docs/superpowers/specs/2026-09-04-roleta-da-sorte-design.md
 *   §4 (schema: roleta_status_t, roleta_segment_t, roleta_spin_status_t, roleta_prize_status_t)
 *   §5.1 (endpoints admin, todos em createResponse → json.data)
 *
 * Mexeu no backend? Mexe aqui também — comparação sempre por enum, nunca por
 * string literal (feedback_use_enums_not_strings).
 */

/** Caminho do resource no react-admin. Nome LONGO (como `chains/chainId/drawings`):
 *  `store/providers/getMany.ts` ignora o `mapPath`, então nome curto + mapPath
 *  quebraria `ReferenceField`/`ReferenceInput`. */
export const ROLETA_RESOURCE = 'chains/chainId/roletas';

/** Caminho do relatório (createReportSummaryResponse → data[0].data / data[0].summaryData). */
export const ROLETA_REPORT_RESOURCE = 'chains/chainId/reports/roleta';

/** Acima disso o rótulo some no celular — é aviso, não bloqueio. */
export const RECOMMENDED_MAX_SEGMENTS = 8;

/** Teto duro do editor: mais que isso a roda vira um borrão. */
export const MAX_SEGMENTS = 12;

/** `roleta_segment.label` é varchar(24) no banco. */
export const MAX_SEGMENT_LABEL_LENGTH = 24;

export enum RoletaStatus {
  Draft = 'draft',
  Active = 'active',
  Inactive = 'inactive',
  Closed = 'closed',
}

export namespace RoletaStatus {
  export const translateStatus = (status: RoletaStatus): string => {
    switch (status) {
      case RoletaStatus.Draft:
        return 'Rascunho';
      case RoletaStatus.Active:
        return 'Ativa';
      case RoletaStatus.Inactive:
        return 'Inativa';
      case RoletaStatus.Closed:
        return 'Encerrada';
      default:
        return '';
    }
  };

  export const roletaStatusChoices = Object.values(RoletaStatus)
    .filter((value) => typeof value === 'string')
    .map((status) => ({
      id: status as string,
      name: RoletaStatus.translateStatus(status as RoletaStatus),
    }));

  /** Status em que a roleta ainda aceita ser vinculada a um brinde novo. */
  export const linkableStatuses: RoletaStatus[] = [RoletaStatus.Draft, RoletaStatus.Active];
}

export enum RoletaSegmentKind {
  Prize = 'prize',
  Nothing = 'nothing',
  ExtraSpin = 'extra_spin',
}

export namespace RoletaSegmentKind {
  export const translateKind = (kind: RoletaSegmentKind): string => {
    switch (kind) {
      case RoletaSegmentKind.Prize:
        return 'Prêmio';
      case RoletaSegmentKind.Nothing:
        return 'Não foi dessa vez';
      case RoletaSegmentKind.ExtraSpin:
        return 'Gire de novo';
      default:
        return '';
    }
  };

  export const roletaSegmentKindChoices = Object.values(RoletaSegmentKind)
    .filter((value) => typeof value === 'string')
    .map((kind) => ({
      id: kind as string,
      name: RoletaSegmentKind.translateKind(kind as RoletaSegmentKind),
    }));
}

export enum RoletaSpinStatus {
  Available = 'available',
  Spinning = 'spinning',
  Spun = 'spun',
  Expired = 'expired',
}

export namespace RoletaSpinStatus {
  export const translateStatus = (status: RoletaSpinStatus): string => {
    switch (status) {
      case RoletaSpinStatus.Available:
        return 'Aguardando giro';
      case RoletaSpinStatus.Spinning:
        return 'Girando';
      case RoletaSpinStatus.Spun:
        return 'Girou';
      case RoletaSpinStatus.Expired:
        return 'Venceu sem girar';
      default:
        return '';
    }
  };
}

export enum RoletaPrizeStatus {
  Pending = 'pending',
  Awarding = 'awarding',
  Delivered = 'delivered',
  Failed = 'failed',
}

export namespace RoletaPrizeStatus {
  export const translateStatus = (status: RoletaPrizeStatus): string => {
    switch (status) {
      case RoletaPrizeStatus.Pending:
        return 'Prêmio sendo liberado';
      case RoletaPrizeStatus.Awarding:
        return 'Prêmio sendo liberado';
      case RoletaPrizeStatus.Delivered:
        return 'Prêmio entregue';
      case RoletaPrizeStatus.Failed:
        return 'Falha na entrega';
      default:
        return '';
    }
  };
}

/** Fatia da roda. O array inteiro vai num PUT próprio (`.../segments`) porque o
 *  `update.ts` do admin só manda campo alterado e compara array por `isEqual`. */
export interface RoletaSegment {
  /** Ausente enquanto a fatia é só rascunho local (ainda não salva). */
  id?: string;
  roletaId?: string;
  segmentIndex: number;
  kind: RoletaSegmentKind;
  label: string;
  /** Vazio = cor automática derivada da cor do app da rede. */
  color?: string | null;
  /** 1-2 caracteres (emoji). */
  icon?: string | null;
  /** Peso relativo; a % mostrada ao operador é weight / Σ pesos habilitados. */
  weight: number;
  /** Só quando kind = prize: produto do brinde de prêmios da roleta. */
  giftProductId?: string | null;
  /** display-only (preenchido pelo editor após escolher o prêmio). */
  giftProductName?: string | null;
  /** Estoque global. null = ilimitado. */
  maxWins?: number | null;
  /** Quantas vezes a fatia já foi sorteada — fatia com giros não é removida, só desativada. */
  winsCount?: number;
  /** Teto por CPF. null = sem teto. */
  maxWinsPerCustomer?: number | null;
  isEnabled: boolean;
  /** Custo manual por prêmio sem cupom (desconto/número/palpite). */
  estimatedCost?: number | null;
}

/** Bloco `stats` do GET de detalhe (§5.1). */
export interface RoletaStats {
  segmentsCount?: number;
  spinsGranted?: number;
  spinsUsed?: number;
  spinsAvailable?: number;
  spinsExpired?: number;
  prizesDelivered?: number;
  prizesPending?: number;
  extraSpins?: number;
  nothingCount?: number;
  uniqueCustomers?: number;
  totalCost?: number;
  costPerSpin?: number;
}

export interface Roleta {
  id: string;
  chainId: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  centerImageUrl?: string | null;
  regulationUrl?: string | null;
  startAt: string;
  endAt: string;
  /** Validade do giro em dias. Vazio = vale até o fim da roleta. */
  spinValidityDays?: number | null;
  maxExtraSpinsPerTicket?: number | null;
  status: RoletaStatus;
  segments?: RoletaSegment[];
  stats?: RoletaStats;
  createdAt?: string;
  updatedAt?: string;
}

/** Linha da tabela do painel "Simular 1.000 giros" (§4.4 do 03-UX-ADMIN). */
export interface RoletaSimulationSegment {
  segmentId?: string;
  segmentIndex: number;
  label?: string;
  kind?: RoletaSegmentKind;
  weight?: number;
  /** 0..1 — quando ausente, o painel calcula pelos pesos. */
  expectedPct?: number;
  /** 0..1 — quando ausente, o painel calcula por count/draws. */
  simulatedPct?: number;
  count?: number;
  unitCost?: number;
  totalCost?: number;
  maxWins?: number | null;
  remainingWins?: number | null;
}

export interface RoletaSimulationResult {
  draws: number;
  segments: RoletaSimulationSegment[];
  expectedCostPerSpin?: number;
  totalCost?: number;
}

/** Linha da aba "Giros" (GET .../roletas/{id}/spins). Vem em snake_case, como
 *  o `guesses` do bolão (BolaoGuessesTab). */
export interface RoletaSpinRow {
  id: string;
  customer_id: string;
  name: string | null;
  cellphone: string | null;
  cpf: string | null;
  status: RoletaSpinStatus;
  segment_index: number | null;
  segment_label: string | null;
  segment_kind: RoletaSegmentKind | null;
  prize_status: RoletaPrizeStatus | null;
  prize_name: string | null;
  source: string | null;
  created_at: string;
  expires_at: string | null;
  spun_at: string | null;
}

export interface RoletaSpinsResponse {
  roleta: { id: string; name: string; status: RoletaStatus };
  total: number;
  page: number;
  perPage: number;
  spins: RoletaSpinRow[];
}

/**
 * Códigos de recusa da ativação (§5.1). O backend devolve 422 com
 * `{ error, message }`; o texto abaixo é o fallback quando a mensagem não vem.
 */
export const ROLETA_ACTIVATION_ERRORS: Record<string, string> = {
  MISSING_SPIN_GIFT: 'Nenhum brinde ativo concede giro nesta roleta. Crie o brinde de giro antes de ativar.',
  MISSING_PRIZE_GIFT: 'Existem fatias de prêmio, mas nenhum brinde de prêmios está vinculado a esta roleta.',
  SEGMENT_PRODUCT_MISMATCH: 'Uma fatia de prêmio aponta para um prêmio que não pertence ao brinde de prêmios desta roleta.',
  ROLETA_NEEDS_SEGMENTS: 'Cadastre pelo menos 2 fatias ativas antes de ativar.',
  ROLETA_NEEDS_UNLIMITED_SEGMENT: 'Pelo menos uma fatia ativa precisa ter estoque ilimitado — senão a roleta pode ficar sem opção.',
  ROLETA_WINDOW_TOO_SHORT: 'A data de fim precisa estar pelo menos 5 minutos à frente de agora.',
  CHAIN_GIFT_DISABLED: 'A rede está sem o Clube de Vantagens habilitado.',
};
